import { motion } from "framer-motion";
import { Eye, BarChart3, Globe, Activity, TrendingUp, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function Observatory() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="observatory-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-purple-600 rounded-full filter blur-[128px] animate-pulse" />
          <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-violet-600 rounded-full filter blur-[128px] animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-6">
              <Eye className="w-4 h-4" />
              <span>Global Intelligence</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Workforce <span className="text-purple-500">Observatory</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-8">
              Global workforce monitoring, intelligence dashboards, predictive analytics, 
              and strategic monitoring systems for data-driven decisions.
            </p>

            <Link to="/observatories">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white gap-2 px-8 py-6 text-lg">
                Explore Observatory
                <ChevronRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Globe, title: "Global Monitoring", description: "Real-time workforce trends worldwide" },
              { icon: BarChart3, title: "Intelligence Dashboards", description: "Comprehensive analytics views" },
              { icon: Activity, title: "Predictive Analytics", description: "AI-powered forecasting" },
              { icon: TrendingUp, title: "Strategic Insights", description: "Data-driven recommendations" }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all"
              >
                <feature.icon className="w-10 h-10 text-purple-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Access Global Intelligence
          </h2>
          <p className="text-white/60 mb-8">
            Gain insights into global workforce trends and make data-driven decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/observatories/global">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white gap-2">
                View Dashboard
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Request Access
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
